import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { PageHeader } from '@/components/common/PageHeader'
import { CharCountTextarea } from '@/components/forms/CharCountTextarea'
import { ConstraintsInput } from '@/components/forms/ConstraintsInput'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useCreateRun } from '@/api/runs'
import type { CreateRunRequest } from '@/lib/types'
import { parseAdoUrl } from '@/lib/utils'

interface FormErrors {
  repositoryUrl?: string
  branch?: string
  goal?: string
}

const RUNNER_PROFILES = [
  { value: 'default', label: 'Default' },
  { value: 'large', label: 'Large (High Memory)' },
  { value: 'gpu', label: 'GPU Enabled' },
]

const MAX_GOAL_LENGTH = 5000
const MIN_GOAL_LENGTH = 10

/**
 * Create new run page with form validation.
 * Route: /#/runs/new
 */
export function CreateRunPage() {
  const navigate = useNavigate()
  const createRun = useCreateRun()

  // Form state
  const [repositoryUrl, setRepositoryUrl] = useState('')
  const [branch, setBranch] = useState('main')
  const [goal, setGoal] = useState('')
  const [constraints, setConstraints] = useState<string[]>([])
  const [runner, setRunner] = useState('default')
  const [workItemId, setWorkItemId] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    // Repository URL validation
    if (!repositoryUrl.trim()) {
      newErrors.repositoryUrl = 'Repository URL is required'
    } else {
      try {
        new URL(repositoryUrl)
        // Validate it's an ADO URL and can be parsed
        const parsed = parseAdoUrl(repositoryUrl)
        if (!parsed) {
          newErrors.repositoryUrl = 'Please enter a valid Azure DevOps repository URL'
        }
      } catch {
        newErrors.repositoryUrl = 'Please enter a valid URL'
      }
    }

    // Branch validation
    if (!branch.trim()) {
      newErrors.branch = 'Branch is required'
    }

    // Goal validation
    if (!goal.trim()) {
      newErrors.goal = 'Goal is required'
    } else if (goal.trim().length < MIN_GOAL_LENGTH) {
      newErrors.goal = `Goal must be at least ${MIN_GOAL_LENGTH} characters`
    } else if (goal.length > MAX_GOAL_LENGTH) {
      newErrors.goal = `Goal must be less than ${MAX_GOAL_LENGTH} characters`
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    if (!validateForm()) {
      return
    }

    // Parse ADO URL to extract project and repository
    const adoParts = parseAdoUrl(repositoryUrl.trim())
    if (!adoParts) {
      setSubmitError('Invalid Azure DevOps repository URL')
      return
    }

    const request: CreateRunRequest = {
      repo_url: repositoryUrl.trim(),
      base_branch: branch.trim(),
      goal: goal.trim(),
      constraints: constraints.filter((c) => c.trim()),
      ado_project: adoParts.project,
      ado_repo_id: adoParts.repository,
      runner_profile: runner !== 'default' ? runner : undefined,
      work_item_id: workItemId.trim() || undefined,
    }

    try {
      const result = await createRun.mutateAsync(request)
      navigate(`/runs/${result.run_id}`)
    } catch (error) {
      console.error('Failed to create run:', error)
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'Failed to create run. Please try again.'
      )
    }
  }

  const handleCancel = () => {
    navigate('/runs')
  }

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        {/* Back Link */}
        <Button variant="ghost" asChild>
          <Link to="/runs">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Runs
          </Link>
        </Button>

        <PageHeader title="Create New Run" />

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Repository Section */}
          <Card>
            <CardHeader>
              <CardTitle>Repository</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="repositoryUrl">
                  Repository URL <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="repositoryUrl"
                  placeholder="https://dev.azure.com/org/project/_git/my-repository"
                  value={repositoryUrl}
                  onChange={(e) => {
                    setRepositoryUrl(e.target.value)
                    if (errors.repositoryUrl) {
                      setErrors((prev) => ({ ...prev, repositoryUrl: undefined }))
                    }
                  }}
                  className={errors.repositoryUrl ? 'border-red-500' : ''}
                />
                {errors.repositoryUrl && (
                  <p className="text-sm text-red-500">⚠️ {errors.repositoryUrl}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="branch">
                  Branch <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="branch"
                  placeholder="main"
                  value={branch}
                  onChange={(e) => {
                    setBranch(e.target.value)
                    if (errors.branch) {
                      setErrors((prev) => ({ ...prev, branch: undefined }))
                    }
                  }}
                  className={errors.branch ? 'border-red-500' : ''}
                />
                {errors.branch && (
                  <p className="text-sm text-red-500">⚠️ {errors.branch}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Task Section */}
          <Card>
            <CardHeader>
              <CardTitle>Task</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="goal">
                  Goal <span className="text-red-500">*</span>
                </Label>
                <CharCountTextarea
                  id="goal"
                  placeholder="Describe what you want the agent to accomplish..."
                  value={goal}
                  onChange={(e) => {
                    setGoal(e.target.value)
                    if (errors.goal) {
                      setErrors((prev) => ({ ...prev, goal: undefined }))
                    }
                  }}
                  maxLength={MAX_GOAL_LENGTH}
                  rows={5}
                  className={errors.goal ? 'border-red-500' : ''}
                />
                {errors.goal && (
                  <p className="text-sm text-red-500">⚠️ {errors.goal}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Constraints</Label>
                <ConstraintsInput
                  value={constraints}
                  onChange={setConstraints}
                  placeholder="e.g., scope:path:src/auth/**"
                />
              </div>
            </CardContent>
          </Card>

          {/* Configuration Section */}
          <Card>
            <CardHeader>
              <CardTitle>Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="runner">Runner Profile</Label>
                  <Select value={runner} onValueChange={setRunner}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select runner profile" />
                    </SelectTrigger>
                    <SelectContent>
                      {RUNNER_PROFILES.map((profile) => (
                        <SelectItem key={profile.value} value={profile.value}>
                          {profile.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="workItemId">Work Item ID (optional)</Label>
                  <Input
                    id="workItemId"
                    placeholder="AB#12345"
                    value={workItemId}
                    onChange={(e) => setWorkItemId(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit Error */}
          {submitError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{submitError}</p>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={createRun.isPending}>
              {createRun.isPending ? 'Creating...' : 'Create Run'}
            </Button>
          </div>
        </form>
      </div>
    </PageShell>
  )
}

export default CreateRunPage
